import { createApp } from "vue";
import { createPinia } from "pinia";

import PrimeVue from "primevue/config";
import Aura from "@primeuix/themes/aura";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";

import "primeicons/primeicons.css";
import "@/assets/main.css";

import App from "@/App.vue";
import { router } from "@/router";

const app = createApp(App);

app.use(createPinia());
app.use(router);

app.use(PrimeVue, {
  license: "eyJpZCI6Ijg4MGJjZjhlLWRiMDktNGZkNS05MzEzLWE0NzE1NDQ3ZTRkMSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODQ1NjIxNDYsImV4cCI6MTgxNjA5ODE0Nn0.ZyPB9x4TV31nPKN0uB9PI63VC-hOQ4mgApbF2PAzTTdRCJSRcSH5qksfUW0GCp8mYvXlTggiagf0OnHaC2dxBA",
  theme: {
    preset: Aura,
    options: {
      // Matches the class toggled by the theme store and pre-applied in index.html.
      darkModeSelector: ".dykkerbutikken-dark",
      // Keep PrimeVue's generated styles ahead of our own layer so app CSS can override them.
      cssLayer: {
        name: "primevue",
        order: "theme, base, primevue, dykkerbutikken",
      },
    },
  },
  ripple: true,
  // Component texts (paginator, date picker, empty states) in Danish; the app's own strings are Danish too.
  locale: {
    accept: "Ja",
    reject: "Nej",
    choose: "Vælg",
    upload: "Upload",
    cancel: "Annullér",
    clear: "Ryd",
    apply: "Anvend",
    emptyMessage: "Ingen resultater",
    emptySearchMessage: "Ingen resultater fundet",
    emptyFilterMessage: "Ingen resultater fundet",
    emptySelectionMessage: "Intet valgt",
    selectionMessage: "{0} valgt",
    searchMessage: "{0} resultater",
    noFileChosenMessage: "Ingen fil valgt",
    pending: "Afventer",
    startsWith: "Begynder med",
    contains: "Indeholder",
    notContains: "Indeholder ikke",
    endsWith: "Slutter med",
    equals: "Er lig med",
    notEquals: "Er ikke lig med",
    noFilter: "Intet filter",
    lt: "Mindre end",
    lte: "Mindre end eller lig med",
    gt: "Større end",
    gte: "Større end eller lig med",
    dateIs: "Dato er",
    dateIsNot: "Dato er ikke",
    dateBefore: "Dato er før",
    dateAfter: "Dato er efter",
    matchAll: "Alle skal passe",
    matchAny: "Én skal passe",
    addRule: "Tilføj regel",
    removeRule: "Fjern regel",
    weak: "Svag",
    medium: "Middel",
    strong: "Stærk",
    passwordPrompt: "Indtast en adgangskode",
    dayNames: ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"],
    dayNamesShort: ["søn", "man", "tir", "ons", "tor", "fre", "lør"],
    dayNamesMin: ["S", "M", "T", "O", "T", "F", "L"],
    monthNames: ["januar", "februar", "marts", "april", "maj", "juni", "juli", "august", "september", "oktober", "november", "december"],
    monthNamesShort: ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"],
    today: "I dag",
    weekHeader: "Uge",
    firstDayOfWeek: 1,
    dateFormat: "dd-mm-yy",
    am: "am",
    pm: "pm",
    aria: {
      trueLabel: "Sand",
      falseLabel: "Falsk",
      nullLabel: "Ikke valgt",
      star: "1 stjerne",
      stars: "{star} stjerner",
      selectAll: "Vælg alle",
      unselectAll: "Fravælg alle",
      close: "Luk",
      previous: "Forrige",
      next: "Næste",
      navigation: "Navigation",
      scrollTop: "Til toppen",
      moveTop: "Flyt til toppen",
      moveUp: "Flyt op",
      moveDown: "Flyt ned",
      moveBottom: "Flyt til bunden",
      moveToTarget: "Flyt til mål",
      moveToSource: "Flyt til kilde",
      moveAllToTarget: "Flyt alle til mål",
      moveAllToSource: "Flyt alle til kilde",
      pageLabel: "Side {page}",
      firstPageLabel: "Første side",
      lastPageLabel: "Sidste side",
      nextPageLabel: "Næste side",
      prevPageLabel: "Forrige side",
      rowsPerPageLabel: "Rækker per side",
      jumpToPageDropdownLabel: "Gå til side",
      jumpToPageInputLabel: "Gå til side",
      selectRow: "Vælg række",
      unselectRow: "Fravælg række",
      expandRow: "Fold række ud",
      collapseRow: "Fold række sammen",
      showFilterMenu: "Vis filtermenu",
      hideFilterMenu: "Skjul filtermenu",
      filterOperator: "Filteroperator",
      filterConstraint: "Filterbetingelse",
      editRow: "Redigér række",
      saveEdit: "Gem",
      cancelEdit: "Annullér",
      listView: "Listevisning",
      gridView: "Gittervisning",
      slide: "Dias",
      slideNumber: "{slideNumber}",
      zoomImage: "Zoom billede",
      zoomIn: "Zoom ind",
      zoomOut: "Zoom ud",
      rotateRight: "Rotér til højre",
      rotateLeft: "Rotér til venstre",
      listLabel: "Liste",
      selectColor: "Vælg en farve",
      removeLabel: "Fjern",
      browseFiles: "Gennemse filer",
      maximizeLabel: "Maksimér",
    },
  },
});

app.use(ToastService);
app.use(ConfirmationService);
app.directive("tooltip", Tooltip);

app.mount("#app");

